import { z } from "zod";
import { OAuthServiceType } from "@common/constants";
import { ChartOfAccountsTemplateUploadResponse } from "@common/schemas/chart-of-accounts-template-upload.schema";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import {
    chartOfAccountsTemplateUploadErrorMessage,
    useParseChartOfAccountsTemplateFromQuickbooksMutation,
} from "@api/coaTemplateUpload.api";
import { useOAuthPopup } from "@hooks/useOAuthPopup";

const IMPORT_FAILED_MESSAGE =
    "Failed to import your Chart of Accounts from QuickBooks Online";

const QuickbooksAdditionalParamsSchema = z.object({ realmId: z.string() });

interface Params {
    onImported: (chart: ChartOfAccountsTemplateUploadResponse) => void;
}

export function useImportChartOfAccountsTemplateFromQuickbooks({
    onImported,
}: Params) {
    const { error: errorToast } = useToaster();
    const parseFromQuickbooks =
        useParseChartOfAccountsTemplateFromQuickbooksMutation();

    const { open, isPopupOpen } = useOAuthPopup({
        service: OAuthServiceType.QUICKBOOKS,
        serviceLabel: "QuickBooks",
        onAuthorized: ({ code, additionalParams }) => {
            const params =
                QuickbooksAdditionalParamsSchema.safeParse(additionalParams);
            if (!params.success) {
                errorToast(IMPORT_FAILED_MESSAGE);
                return;
            }
            parseFromQuickbooks.mutate(
                { authCode: code, realmId: params.data.realmId },
                {
                    onSuccess: onImported,
                    onError: (error) =>
                        errorToast(
                            chartOfAccountsTemplateUploadErrorMessage({
                                error,
                                fallback: IMPORT_FAILED_MESSAGE,
                            }),
                        ),
                },
            );
        },
    });

    return {
        start: open,
        isPending: isPopupOpen || parseFromQuickbooks.isPending,
    };
}
